import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Fire } from '@phosphor-icons/react';
import { getEffectivePrice, hasProPrice } from './shopUtils';
import { Countdown, LiveViewers, discountInfo, getProductBrand, getProductCategoryName, saleEndDate } from './productExtras';

export const ProductCard = ({ p, showCategory = true, compact = false, kioskMode = false, colors, categories, getLink, isInWishlist, toggleWishlist, store, addItem, storeSlug, isPro }) => {
  const catName = showCategory ? getProductCategoryName(p, categories) : null;
  const brand = getProductBrand(p, categories);
  const isPromo = p.is_promo && p.sale_price;
  const isOutOfStock = p.stock_status === 'outofstock';
  const effectivePrice = getEffectivePrice(p, isPro);
  const isProPrice = hasProPrice(p, isPro);
  const disc = discountInfo(p);
  const saleEnd = saleEndDate(p);
  const firstImg = p.featured_image || p.images?.[0];
  const secondImg = (p.images && p.images.length > 1)
    ? p.images.find((im, idx) => idx > 0 && im && im !== firstImg)
    : null;

  return (
    <Link to={getLink(`/product/${p.slug}`)}
      className={`group block bg-white border border-slate-100 hover:shadow-xl transition-all rounded-xl overflow-hidden`}
      data-testid={`product-card-${p.id}`}>
      <div className={`${compact ? 'aspect-[4/3]' : 'aspect-square'} overflow-hidden bg-white relative`} data-name={p.name?.[0] || '?'}>
        {firstImg ? (
          <>
            <img src={firstImg} alt={p.name}
              className={`absolute inset-0 w-full h-full object-contain p-3 transition-all duration-700 ease-out ${secondImg ? 'group-hover:opacity-0 group-hover:scale-105' : 'group-hover:scale-110'} ${isOutOfStock ? 'opacity-50 grayscale-[40%]' : ''}`} loading="lazy"
              onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.classList.add('product-img-fallback'); }} />
            {secondImg && (
              <img src={secondImg} alt={`${p.name} - vue 2`}
                className={`absolute inset-0 w-full h-full object-contain p-3 opacity-0 scale-105 transition-all duration-700 ease-out group-hover:opacity-100 group-hover:scale-110 ${isOutOfStock ? 'grayscale-[40%]' : ''}`} loading="lazy"
                onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
            )}
          </>
        ) : null}
        {!firstImg && (
          <div className="w-full h-full flex items-center justify-center bg-slate-100">
            <span className="text-3xl font-semibold text-slate-200">{p.name?.[0]}</span>
          </div>
        )}
        {isPromo && (
          <span className="absolute top-2.5 left-2.5 px-3 py-1.5 text-white text-[12px] font-semibold rounded-full shadow-md"
            style={{ backgroundColor: colors.primary }}>
            {p.promo_label || `-${Math.round((1 - p.sale_price / p.price) * 100)}%`}
          </span>
        )}
        {p.is_featured && !isPromo && (
          <span className="absolute top-2.5 left-2.5 px-2.5 py-1.5 text-white text-[11px] font-bold rounded-full bg-amber-500 flex items-center gap-1 shadow-md">
            <Fire size={13} weight="fill" /> Best
          </span>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="bg-black/70 text-white text-[12px] font-bold px-4 py-2 rounded-lg uppercase tracking-wide">Rupture de stock</span>
          </div>
        )}
        <button className={`absolute top-2.5 right-2.5 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transition-all shadow-sm ${isInWishlist(p.id) ? 'opacity-100 text-pink-500' : 'text-slate-400 hover:text-pink-500 opacity-0 group-hover:opacity-100'}`}
          onClick={e => { e.preventDefault(); e.stopPropagation(); if (store?.id) toggleWishlist(store.id, p.id); }}
          data-testid={`wishlist-toggle-${p.id}`}>
          <Heart size={17} weight={isInWishlist(p.id) ? 'fill' : 'regular'} />
        </button>
      </div>
      <div className={compact ? 'p-3' : 'p-4'}>
        {!compact && (brand || catName) && (
          <div className="flex items-center gap-2 mb-2 min-h-[20px]">
            {brand && (
              <span className="inline-flex items-center gap-1.5">
                <img src={brand.logo} alt={brand.name} className="h-4 max-w-[44px] object-contain" loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                <span className="text-[11px] font-semibold text-slate-700">{brand.name}</span>
              </span>
            )}
            {catName && <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400 truncate">{catName}</span>}
          </div>
        )}
        <h3 className={`${compact ? 'text-[13px]' : 'text-[14px]'} font-semibold text-slate-800 line-clamp-2 mb-2 min-h-[40px]`}>{p.name}</h3>
        <div className="mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`${compact ? 'text-[16px]' : 'text-[18px]'} font-semibold`} style={{ color: isProPrice ? '#7c3aed' : colors.primary }}>
              {effectivePrice.toFixed(2)} €
            </span>
            {(isPromo || isProPrice) && <span className="text-[13px] text-slate-400 line-through">{parseFloat(p.price).toFixed(2)} €</span>}
            {isProPrice && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" data-testid={`pro-badge-${p.id}`}>Pro</span>
            )}
            {disc && !isProPrice && (
              <span className="text-[11px] font-bold text-white px-1.5 py-0.5 rounded" style={{ backgroundColor: colors.accent || '#FF004D' }} data-testid={`discount-pct-${p.id}`}>-{disc.percent}%</span>
            )}
            <small className="text-[11px] text-slate-400 font-normal">TTC</small>
          </div>
          {disc && !isProPrice && (
            <div className="text-[11.5px] font-semibold mt-0.5" style={{ color: colors.accent || '#FF004D' }}>Économisez {disc.amount} €</div>
          )}
          {isPromo && saleEnd && <div className="mt-1.5"><Countdown end={saleEnd} accent={colors.accent} compact /></div>}
          {(() => {
            const ppLaterEnabled = !!store?.settings?.paypal_pay_later_enabled;
            const minAmt = parseFloat(store?.settings?.paypal_pay_later_min || 600);
            if (!ppLaterEnabled || effectivePrice < minAmt) return null;
            const monthly = (effectivePrice / 4).toFixed(2).replace('.', ',');
            return (
              <div className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded bg-[#FFC439]/25 border border-[#FFC439]/40 text-[10px] font-semibold text-[#003087]" data-testid={`paylater-card-badge-${p.id}`}>
                <span className="font-semibold">Pay<span className="italic">Pal</span></span>
                <span>4× sans frais · {monthly} €/mois</span>
              </div>
            );
          })()}
        </div>
        {!compact && <div className="mb-3 -mt-0.5"><LiveViewers id={p.id} compact /></div>}
        {isPromo && p.promo_quantity_limit && (
          <p className="text-[11px] text-orange-600 font-semibold mb-2">Plus que {p.promo_quantity_limit} en stock !</p>
        )}
        {!compact && !kioskMode && (
          <button onClick={e => { e.preventDefault(); if (!isOutOfStock) addItem({ ...p, storeSlug }); }}
            className={`w-full py-2.5 text-[13px] font-bold text-white rounded-xl transition-all ${isOutOfStock ? 'opacity-50 cursor-not-allowed' : 'hover:brightness-110 active:scale-[0.97]'}`}
            style={{ backgroundColor: isOutOfStock ? '#94a3b8' : colors.primary }}
            data-testid={`add-to-cart-${p.id}`}>
            {isOutOfStock ? 'Indisponible' : 'Ajouter au panier'}
          </button>
        )}
      </div>
    </Link>
  );
};

export const ProductRow = ({ p, colors, getLink, addItem, storeSlug, isPro, store }) => {
  const isOutOfStock = p.stock_status === 'outofstock';
  const isPromo = p.is_promo && p.sale_price;
  const disc = discountInfo(p);
  const effectivePrice = getEffectivePrice(p, isPro);
  const isProPrice = hasProPrice(p, isPro);

  return (
    <Link to={getLink(`/product/${p.slug}`)}
      className="flex items-center gap-4 bg-white border border-slate-100 hover:shadow-lg transition-all rounded-xl overflow-hidden p-3 group"
      data-testid={`product-row-${p.id}`}>
      <div className="w-[90px] h-[90px] flex-shrink-0 bg-slate-50 rounded-lg overflow-hidden">
        {(p.featured_image || p.images?.[0]) ? (
          <img src={p.featured_image || p.images[0]} alt={p.name} className={`w-full h-full object-contain p-1.5 group-hover:scale-110 transition-transform ${isOutOfStock ? 'opacity-50 grayscale-[40%]' : ''}`} loading="lazy"
            onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><span className="text-xl font-semibold text-slate-200">{p.name?.[0]}</span></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-[14px] font-semibold text-slate-800 line-clamp-1">{p.name}</h3>
        {p.category && <span className="text-[11px] font-medium uppercase tracking-wider" style={{ color: colors.primary }}>{p.category}</span>}
        <div className="mt-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[17px] font-semibold" style={{ color: isProPrice ? '#7c3aed' : colors.primary }}>{effectivePrice.toFixed(2)} €</span>
            {(isPromo || isProPrice) && <span className="text-[12px] text-slate-400 line-through">{parseFloat(p.price).toFixed(2)} €</span>}
            {isProPrice && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">Pro</span>}
            <small className="text-[11px] text-slate-400">TTC</small>
            {isOutOfStock && <span className="text-[11px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Rupture</span>}
          </div>
          {disc && <span className="text-[11px] font-semibold block mt-0.5" style={{ color: colors.accent || '#FF004D' }}>-{disc.percent}% · Économisez {disc.amount} €</span>}
          {(() => {
            const ppLaterEnabled = !!store?.settings?.paypal_pay_later_enabled;
            const minAmt = parseFloat(store?.settings?.paypal_pay_later_min || 600);
            if (!ppLaterEnabled || effectivePrice < minAmt) return null;
            const monthly = (effectivePrice / 4).toFixed(2).replace('.', ',');
            return (
              <div className="inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded bg-[#FFC439]/25 border border-[#FFC439]/40 text-[10px] font-semibold text-[#003087]">
                <span className="font-semibold">Pay<span className="italic">Pal</span></span>
                <span>4× — {monthly} €/mois</span>
              </div>
            );
          })()}
        </div>
      </div>
      <button onClick={e => { e.preventDefault(); if (!isOutOfStock) addItem({ ...p, storeSlug }); }}
        className={`flex-shrink-0 px-4 py-2 text-[12px] font-bold text-white rounded-xl ${isOutOfStock ? 'opacity-40 cursor-not-allowed' : 'hover:brightness-110'}`}
        style={{ backgroundColor: isOutOfStock ? '#94a3b8' : colors.primary }}>
        {isOutOfStock ? 'Indisponible' : 'Ajouter'}
      </button>
    </Link>
  );
};
